import { useEffect, useState } from "react";
import { Card } from "./Card";
import { Button } from "./Button";
import { otherAppApi } from "../../services/userApi";

/**
 * The other half of the app exists, said once (#302).
 *
 * The reverse of the phone's version, and cheaper to justify: a desk user
 * is missing offline field entry, which is the one thing the browser
 * cannot do and the entire reason the phone app exists. Somebody
 * recording inspections from memory at a kitchen table after the fact is
 * losing detail nobody can get back.
 *
 * ── A CARD, NOT A MODAL ──
 *
 * It appears at most twice in an account's life and it is not urgent.
 * Interrupting somebody who opened the dashboard to do a job, in order
 * to make a suggestion, is how suggestions get trained away.
 *
 * ── THE WORDING COMES FROM THE SERVER ──
 *
 * Deliberately, and it is why this component is thin. The decision and
 * the pitch live in services/other_app.py so the two apps cannot drift
 * into two different ideas of what this person is missing.
 *
 * ── DISMISSING IS THE POINT, NOT AN ESCAPE HATCH ──
 *
 * "Twice, then never." The dismiss spends one of the two, so it is a
 * real button rather than a small x, and the card goes immediately
 * rather than waiting on the server: the person has answered.
 */
export function OtherAppCard() {
  const [prompt, setPrompt] = useState(null);

  useEffect(() => {
    let cancelled = false;
    // Best-effort: a suggestion nobody asked for must never be the reason
    // a dashboard fails to load.
    otherAppApi.check("web")
      .then((r) => !cancelled && setPrompt(r.prompt || null))
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!prompt) return null;

  function dismiss() {
    setPrompt(null);
    otherAppApi.dismiss("web").catch(() => {});
  }

  return (
    <Card title={prompt.headline} className="section-spacing">
      <p style={{ marginTop: 0 }}>{prompt.detail}</p>
      <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
        {/* No link to an app store: this app does not know which phone
            they have, and a wrong store is worse than a sentence telling
            them to search for it. The pitch names what the phone DOES,
            which is the part that decides whether they go looking. */}
        <Button variant="secondary" onClick={dismiss}>Not now</Button>
      </div>
      <p style={{ marginBottom: 0, color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
        {/* Says the restraint out loud. Somebody who knows this is nearly
            over is likelier to read it than to learn to scroll past. */}
        You will see this at most twice.
      </p>
    </Card>
  );
}
